<template lang="pug">
div(v-if="hasPayPalDetails" class="mb-4")
  h3.text-sm.font-semibold.mb-3 PayPal Details
  //- Seller Receivable Breakdown
  div(v-if="sellerBreakdown && sellerBreakdown.gross_amount" class="mb-3 p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg")
    p.text-sm.font-medium
      | Gross: {{ formatCurrency(Number(sellerBreakdown.gross_amount.value), sellerBreakdown.gross_amount.currency_code) }}
    p.text-xs.text-muted-foreground.mt-1
      | Fee: {{ formatCurrency(Number(sellerBreakdown.paypal_fee.value), sellerBreakdown.paypal_fee.currency_code) }} ({{ feePercentage }}%)
    p.text-xs.text-muted-foreground
      | Net: {{ formatCurrency(Number(sellerBreakdown.net_amount.value), sellerBreakdown.net_amount.currency_code) }}
  .grid.grid-cols-2.gap-4
    div(v-if="paypalPayer.name")
      p.text-xs.text-muted-foreground.mb-1 Payer Name
      p.text-sm {{ payerFullName }}
    div(v-if="paypalPayer.email_address")
      p.text-xs.text-muted-foreground.mb-1 Payer Email
      p.text-sm {{ paypalPayer.email_address }}
    div(v-if="paypalPayer.payer_id")
      p.text-xs.text-muted-foreground.mb-1 Payer ID
      p.text-sm.font-mono {{ paypalPayer.payer_id }}
    div(v-if="data.capture_id || data.order_id")
      p.text-xs.text-muted-foreground.mb-1 Transaction
      a(:href="paypalDashboardUrl" target="_blank" class="text-sm text-blue-600 hover:underline inline-flex items-center gap-1")
        | {{ data.capture_id || data.order_id }}
        Icon(name="lucide:external-link" class="w-3 h-3")
    div(v-if="paypalPayer.address?.country_code")
      p.text-xs.text-muted-foreground.mb-1 Country
      p.text-sm {{ paypalPayer.address.country_code }}
</template>

<script lang="ts" setup>
import { computed } from 'vue'

const props = defineProps<{
  data: any
}>()

const paypalPayer = computed(() => props.data?.extra?.paypal_payer || {})
const sellerBreakdown = computed(() => props.data?.extra?.seller_receivable_breakdown || null)

const hasPayPalDetails = computed(() => 
  props.data?.channel === 'PayPal' && (Object.keys(paypalPayer.value).length > 0 || sellerBreakdown.value)
)

const feePercentage = computed(() => {
  if (!sellerBreakdown.value?.paypal_fee?.value || !sellerBreakdown.value?.gross_amount?.value) return '0'
  const fee = Number(sellerBreakdown.value.paypal_fee.value)
  const gross = Number(sellerBreakdown.value.gross_amount.value)
  return ((fee / gross) * 100).toFixed(1)
})

const payerFullName = computed(() => {
  const name = paypalPayer.value.name
  if (!name) return '—'
  return [name.given_name, name.surname].filter(Boolean).join(' ') || '—'
})

const paypalDashboardUrl = computed(() => {
  if (props.data?.capture_id) {
    return `https://www.paypal.com/activity/payment/${props.data.capture_id}`
  }
  if (props.data?.order_id) {
    return `https://www.paypal.com/activity/actions/details/${props.data.order_id}`
  }
  return ''
})

function formatCurrency(amount: number, currency: string) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency
  }).format(amount)
}
</script>